import { pbDelete } from '@/pb'
import { objectMap, useInputState } from '@/utils'
import AddIcon from '@mui/icons-material/Add'
import DataObjectIcon from '@mui/icons-material/DataObject'
import DownloadIcon from '@mui/icons-material/Download'
import NotesIcon from '@mui/icons-material/Notes'
import SearchIcon from '@mui/icons-material/Search'
import ViewListIcon from '@mui/icons-material/ViewList'
import {
  Button,
  Dialog,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Tooltip
} from '@mui/material'
import { DataGrid, Toolbar, ToolbarButton } from '@mui/x-data-grid'
import exceljs from 'exceljs'
import papaparse from 'papaparse'
import { useCallback, useMemo, useState } from 'react'
import Spacer from './Spacer'

const paginationModel = { page: 0, pageSize: 10 }
const initialState = { pagination: { paginationModel } }
const pageSizeOptions = [1, 2, 10, 50, 100]

const searchInColumns = (columns, filter) => row => columns.find(col => String(row[col.field]).toLowerCase().includes(filter.toLowerCase()))


export default function CollectionDataGrid({
  collection,
  columns,
  loading,
  collectionName,
  onSelect,
  editor: Editor,
  editable = !!Editor,
  creatable = !!editable,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  serverPagination = false,
  ...rest
}) {
  const [filter, setFilter] = useInputState('')
  const [editingRowParams, setEditingRowParams] = useState(null)

  const baseRows = useMemo(() => {
    if (Array.isArray(collection)) return collection
    return Object.values(collection ?? {})
  }, [collection])

  const rows = useMemo(() => {
    if (!filter || serverPagination) return baseRows
    return baseRows.filter(searchInColumns(columns, filter))
  }, [baseRows, columns, filter, serverPagination])

  const editorClose = useCallback(() => setEditingRowParams(null), [])

  const slotProps = useMemo(
    () => ({
      toolbar: {
        onFilterChange: setFilter,
        onCreateClick: creatable ? () => setEditingRowParams({ id: undefined, row: {} }) : undefined,
        onDownloadClick: type => {
          const exportRows = rows

          if (type === 'json') {
            const data = exportJSON(exportRows, columns)
            downloadAsFile(collectionName + '.json', data, 'application/json')
          } else if (type === 'csv') {
            const data = exportCSV(exportRows, columns)
            downloadAsFile(collectionName + '.csv', data, 'text/csv')
          } else if (type === 'excel') {
            exportExcel(exportRows, columns, collectionName)
              .then(data => downloadAsFile(collectionName + '.xlsx', data, 'application/vnd.ms-excel'))
              .catch(err => alert(err))
          }
        },
        tableEmpty: serverPagination ? (rowCount ?? 0) === 0 : !rows.length,
      },
    }),
    [setFilter, creatable, rows, columns, collectionName, serverPagination, rowCount]
  )

  return (
    <Paper>
      <DataGrid
        pagination
        paginationMode={serverPagination ? 'server' : 'client'}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        rowCount={serverPagination ? rowCount : rows.length}
        disableRowSelectionExcludeModel
        showToolbar
        disableRowSelectionOnClick
        checkboxSelection={!!onSelect}
        onRowSelectionModelChange={onSelect}
        onRowDoubleClick={editable ? setEditingRowParams : undefined}
        rows={rows}
        columns={columns}
        loading={loading}
        pageSizeOptions={pageSizeOptions}
        slots={slots}
        slotProps={slotProps}
        getRowId={row => row.id}
        {...rest}
      />

      <Dialog fullWidth maxWidth="sm" open={!!editingRowParams && !!Editor} onClose={editorClose}>
        {!!editingRowParams && Editor && (
          <Editor
            {...(console.log(collection, editingRowParams.row, editingRowParams.id, editingRowParams), {})}
            row={Array.isArray(collection) ? editingRowParams.row : collection?.[editingRowParams.id]}
            id={editingRowParams.id}
            onClose={editorClose}
            rows={rows}
            columns={columns}
          />
        )}
      </Dialog>
    </Paper>
  )
}

function CustomToolbar({ onFilterChange, onCreateClick, onDownloadClick, tableEmpty }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const handleOpenClick = event => setAnchorEl(event.currentTarget)
  const handleClose = type => () => {
    setAnchorEl(null)
    if (type)
      setTimeout(() => onDownloadClick(type), 1)
  }

  return (
    <Toolbar>
      <OutlinedInput
        placeholder="Поиск"
        size="small"
        endAdornment={<InputAdornment position="end"><SearchIcon fontSize="small" /></InputAdornment>}
        onChange={onFilterChange}
      />
      <Spacer />
      <Tooltip title="Добваить">
        <span>
          <ToolbarButton onClick={onCreateClick} disabled={!onCreateClick}>
            <AddIcon fontSize="small" />
          </ToolbarButton>
        </span>
      </Tooltip>
      <Tooltip title="Скачать">
        <span>
          <ToolbarButton onClick={handleOpenClick} disabled={tableEmpty}>
            <DownloadIcon fontSize="small" />
          </ToolbarButton>
        </span>
      </Tooltip>


      <Menu
        anchorEl={anchorEl}
        open={open && !tableEmpty}
        onClose={handleClose()}
        anchorOrigin={menuOrigin}
        transformOrigin={menuOrigin}
      >
        <MenuItem onClick={handleClose('excel')}>
          <ListItemIcon>
            <ViewListIcon fontSize="small" />
          </ListItemIcon>
          Excel
        </MenuItem>
        <MenuItem onClick={handleClose('csv')}>
          <ListItemIcon>
            <NotesIcon fontSize="small" />
          </ListItemIcon>
          CSV
        </MenuItem>
        <MenuItem onClick={handleClose('json')}>
          <ListItemIcon>
            <DataObjectIcon fontSize="small" />
          </ListItemIcon>
          JSON
        </MenuItem>
      </Menu>
    </Toolbar>
  )
}

const menuOrigin = {
  vertical: 'top',
  horizontal: 'right',
}

const slots = {
  toolbar: CustomToolbar
}

export const exportJSON = (rows, columns) => {
  const base = columns.reduce((acc, col) => { acc[col.headerName] = col; return acc }, {})
  const out = rows.map(row => objectMap(base, col => row[col.field]))
  return JSON.stringify(out)
}

export const exportCSV = (rows, columns) => {
  const colfields = columns.map(col => col.field)
  const colnames = columns.map(col => col.headerName)
  const lines = [colnames]

  for (const row of rows) {
    lines.push(colfields.map(col => row[col]))
  }

  return papaparse.unparse(lines, {
    header: false,
  })
}

export const exportExcel = (rows, columns, worksheetName = 'data') => {
  const colfields = columns.map(col => col.field)
  const colnames = columns.map(col => col.headerName)

  const wb = new exceljs.Workbook()
  const ws = wb.addWorksheet(worksheetName)
  ws.addRow(colnames)

  for (const row of rows) {
    ws.addRow(colfields.map(col => row[col]))
  }

  columns.forEach((col, i) => ws.getColumn(i + 1).width = col.width / 8)

  return wb.xlsx.writeBuffer()
}

function downloadAsFile(filename, value, contentType) {
  let blob
  if (value instanceof Uint8Array) {
    blob = new Blob([value], { type: contentType })
  } else {
    blob = new Blob([String(value)], { type: contentType })
  }

  var element = document.createElement('a')
  element.href = URL.createObjectURL(blob)
  element.setAttribute('download', filename)
  element.click()
  URL.revokeObjectURL(element.href)
}

export function DeleteButton({ collection, id, confirmMsg, onDelete, ...rest }) {
  return !id ? null : <Button color="secondary" {...rest} onClick={() => pbDelete(collection, id, confirmMsg).then(onDelete)} >Удалить</Button>
}